import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import React, { useState } from 'react'
import { numberWithCommas } from 'src/utils'
import { DEFAULT_PER_SQM_RATES, OnDemandCityConfig } from 'src/utils/onDemand'

import { DurationTierKey } from './OnDemandWizard/helpers'

interface IProps {
  /** When set (city page or parent w/ personalized city), prices come from
   *  the city's `perSqmRates`. When unset, the section falls back to the
   *  partner-published rates so the grid still renders. */
  coverage?: OnDemandCityConfig
  /** Parent-page fallback — first active city's coverage, used when no
   *  city has been resolved yet. Same shape as `coverage` so the duration
   *  toggle works identically. */
  fallbackCoverage?: OnDemandCityConfig
}

const SIZE_KEYS = ['SMALL', 'MEDIUM', 'LARGE'] as const
type SizeKey = (typeof SIZE_KEYS)[number]

/**
 * Estimated floor footprint per bundle tier. Same numbers as the wizard's
 * `volumeToEstimatedM2` so the price the user sees here is mathematically
 * identical to the price they'd see live in the wizard's PriceReveal —
 * single seam, no drift between marketing copy and quote. Also feeds the
 * landing's JSON-LD `offers` range (`index.tsx`).
 */
export const SIZE_TO_M2: Record<SizeKey, number> = {
  SMALL: 2,
  MEDIUM: 4,
  LARGE: 7
}

const SIZE_TO_IMAGE: Record<SizeKey, string> = {
  SMALL: '/images/sizes/small/small-closet-v2.webp',
  MEDIUM: '/images/sizes/medium/medium-2-bedroom-v2.webp',
  LARGE: '/images/sizes/large/large-3-bedroom-v2.webp'
}

const TIERS: ReadonlyArray<{
  key: DurationTierKey
  labelKey:
    | 'SIZES.DURATION_TOGGLE.MONTH'
    | 'SIZES.DURATION_TOGGLE.SIX'
    | 'SIZES.DURATION_TOGGLE.TWELVE'
}> = [
  { key: 'monthToMonth', labelKey: 'SIZES.DURATION_TOGGLE.MONTH' },
  { key: 'sixMonths', labelKey: 'SIZES.DURATION_TOGGLE.SIX' },
  { key: 'twelveMonths', labelKey: 'SIZES.DURATION_TOGGLE.TWELVE' }
]

/**
 * Three size cards with a duration toggle. Pricing is computed as
 * `SIZE_TO_M2[size] × perSqmRates[tier]` — the same formula the wizard's
 * PriceReveal uses. The toggle defaults to `twelveMonths` (cheapest) so
 * the headline numbers match the "desde" framing used elsewhere.
 *
 * Visual treatment mirrors `src/screens/PersonalizedLanding/SizesSection.tsx`:
 *  - Photo at top, hairline border, no shadow
 *  - Editorial eyebrow with numbered prefix (01 · m³ tier)
 *  - Fraunces title + body + italic-Fraunces price
 *  - Live reprice when the duration toggle changes (no animation —
 *    instantly readable, no perceived lag).
 */
const IndicativeSizesGrid = ({ coverage, fallbackCoverage }: IProps) => {
  const { t } = useTranslation('page_almacenamiento_a_domicilio')
  const [tier, setTier] = useState<DurationTierKey>('twelveMonths')

  const rates =
    coverage?.perSqmRates ??
    fallbackCoverage?.perSqmRates ??
    DEFAULT_PER_SQM_RATES
  const rate = rates[tier]

  return (
    <section
      aria-labelledby="sizes-heading"
      className="border-t border-ink-100/15 bg-cream-300 py-20 md:py-28"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
          {t('SIZES.TITLE')}
        </p>
        <h2
          id="sizes-heading"
          className="mt-3 max-w-3xl font-display text-[24px] leading-[1.2] font-medium tracking-[-0.01em] text-ink-700 md:text-[32px]"
        >
          {t('SIZES.TITLE')}
        </h2>

        <div className="mt-6 flex flex-col gap-3 md:mt-8 md:flex-row md:items-center md:gap-5">
          <p className="font-display text-[11px] tracking-[0.18em] text-ink-200 uppercase">
            {t('SIZES.DURATION_TOGGLE.LABEL')}
          </p>
          <div
            role="tablist"
            aria-label={t('SIZES.DURATION_TOGGLE.LABEL')}
            className="inline-flex w-full overflow-hidden rounded-full border border-ink-100/20 bg-white-500 md:w-auto"
          >
            {TIERS.map((t_) => {
              const isActive = t_.key === tier
              return (
                <button
                  key={t_.key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setTier(t_.key)}
                  className={`flex-1 px-4 py-2 text-[12px] font-medium transition-colors md:flex-none md:px-5 md:py-2 md:text-[13px] ${
                    isActive
                      ? 'bg-blue-500 text-white-500'
                      : 'bg-white-500 text-ink-700 hover:text-blue-500'
                  }`}
                >
                  {t(t_.labelKey)}
                </button>
              )
            })}
          </div>
        </div>

        <ul className="mt-10 grid grid-cols-1 gap-5 md:mt-12 md:grid-cols-3 md:gap-6">
          {SIZE_KEYS.map((key, i) => {
            const stepNumber = String(i + 1).padStart(2, '0')
            const monthly = SIZE_TO_M2[key] * rate
            return (
              <li
                key={key}
                className="group flex h-full flex-col overflow-hidden border border-ink-100/12 bg-white-500 transition-colors duration-300 hover:border-blue-500/40"
              >
                <div className="relative aspect-[5/4] overflow-hidden bg-cream-200">
                  <Image
                    src={SIZE_TO_IMAGE[key]}
                    alt={t(`SIZES.${key}.TITLE`)}
                    fill
                    sizes="(max-width: 900px) 100vw, 33vw"
                    style={{ objectFit: 'contain' }}
                    quality={85}
                    className="p-5 transition-transform duration-500 group-hover:scale-[1.03]"
                    loading="lazy"
                  />
                </div>
                <div className="flex flex-1 flex-col gap-3 px-5 py-5 md:px-6 md:py-6">
                  <p className="font-display text-[11px] tracking-[0.18em] text-blue-500 uppercase">
                    <span className="italic">{stepNumber}</span>
                    <span className="mx-2 text-ink-100/40">·</span>
                    <span className="not-italic">
                      {t(`SIZES.${key}.TITLE`)}
                    </span>
                  </p>
                  <h3 className="font-display text-[20px] leading-[1.15] font-medium text-ink-700 md:text-[22px]">
                    {t(`SIZES.${key}.TITLE`)}
                  </h3>
                  <p className="text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
                    {t(`SIZES.${key}.DESCRIPTION`)}
                  </p>
                  <span className="mt-auto pt-2 font-display text-[16px] font-medium text-blue-500 italic md:text-[18px]">
                    {t('SIZES.STARTING_FROM', {
                      price: numberWithCommas(monthly)
                    })}
                  </span>
                </div>
              </li>
            )
          })}
        </ul>
      </div>
    </section>
  )
}

export default React.memo(IndicativeSizesGrid)
